import type { LensesObject } from "@/types/api.types";
import type { ContactLensesData } from "@/types/config.types";
import type {
  LensPrescription,
  PrescriptionData,
} from "@/types/prescription.types";

/**
 * Returns true if a prescription object has at least one non-empty value.
 */
export const hasPrescriptionValues = (
  prescription: LensPrescription | null | undefined,
): boolean =>
  !!prescription &&
  Object.values(prescription).some(
    (v) => v !== "" && v !== undefined && v !== null,
  );

// todo: verify if the prescription is missing some values
export const mapPrescriptions = (
  prescription: PrescriptionData | null | undefined,
): { OD: LensPrescription; OS: LensPrescription } | undefined => {
  const odPrescription = {
    CL_SPHERE_POWER: prescription?.SPH?.OD?.toString(),
    CL_DIAMETER: prescription?.DIA?.OD?.toString(),
    CL_BASE_CURVE: prescription?.BC?.OD?.toString(),
    CL_AXIS: prescription?.AX?.OD?.toString(),
    CL_ADDITION: prescription?.ADD?.OD?.toString(),
    CL_CYLINDER: prescription?.CYL?.OD?.toString(),
  };
  const osPrescription = {
    CL_SPHERE_POWER: prescription?.SPH?.OS?.toString(),
    CL_DIAMETER: prescription?.DIA?.OS?.toString(),
    CL_BASE_CURVE: prescription?.BC?.OS?.toString(),
    CL_AXIS: prescription?.AX?.OS?.toString(),
    CL_ADDITION: prescription?.ADD?.OS?.toString(),
    CL_CYLINDER: prescription?.CYL?.OS?.toString(),
  };
  return prescription ? { OD: odPrescription, OS: osPrescription } : undefined;
};

/**
 * Builds the lensesDataToSend object from contactLensesData.
 * OD/OS are included only if the product exists and the prescription
 * contains at least one non-empty value.
 *
 * @param contactLensesData  - The current contact lenses store data
 * @param prescriptionId     - Optional prescription ID (from MyAccount flow)
 * @param odPrescription     - Override for OD prescription (e.g. merged form data in ManualInputStep)
 * @param osPrescription     - Override for OS prescription (e.g. merged form data in ManualInputStep)
 */
export const buildLensesDataToSend = (
  contactLensesData: ContactLensesData,
  prescription?: PrescriptionData,
  odPrescription?: LensPrescription,
  osPrescription?: LensPrescription,
): LensesObject => {
  const odID = contactLensesData?.OD?.product?.catalogEntryIdentifier?.uniqueId;
  const odQuantity = contactLensesData?.OD?.product?.boxQuantity;
  const osID = contactLensesData?.OS?.product?.catalogEntryIdentifier?.uniqueId;
  const osQuantity = contactLensesData?.OS?.product?.boxQuantity;

  // TODO: get all properties from my account
  const accountPrescription = mapPrescriptions(prescription);

  const resolvedOD =
    odPrescription ??
    accountPrescription?.OD ??
    contactLensesData?.OD?.prescription;

  const resolvedOS =
    osPrescription ??
    accountPrescription?.OS ??
    contactLensesData?.OS?.prescription;

  const hasOD = !!odID && hasPrescriptionValues(resolvedOD);
  const hasOS = !!osID && hasPrescriptionValues(resolvedOS);

  return {
    ...(hasOD && resolvedOD && odQuantity
      ? {
          OD: {
            productId: odID,
            prescription: resolvedOD,
            boxQuantity: odQuantity,
            orderItemId: contactLensesData?.OD?.product?.orderItemId,
            ...(prescription
              ? { prescriptionId: prescription.prescriptionId }
              : {}),
          },
        }
      : {}),
    ...(hasOS && resolvedOS && osQuantity
      ? {
          OS: {
            productId: osID,
            prescription: resolvedOS,
            boxQuantity: osQuantity,
            orderItemId: contactLensesData?.OS?.product?.orderItemId,
            ...(prescription
              ? { prescriptionId: prescription.prescriptionId }
              : {}),
          },
        }
      : {}),
  };
};
